/**
 * Support Stage — Stage 5: Support/hint validation.
 *
 * ARCHITECTURE v4.0 — Phase 2, Step 2.6
 *
 * Validates:
 * - Families that require hints have them available
 * - Support coverage meets policy requirements
 */

import type { ValidationIssue, PipelineStage } from '../issueContract'
import { coach, info } from '../issueContract'
import { getSupportPolicy, isFeatureEnabled } from '../policyEngine'
import type { Question } from '../../../types'

const STAGE: PipelineStage = 5

/**
 * Run support validation on a question.
 */
export function runSupportStage(question: Question): ValidationIssue[] {
  const issues: ValidationIssue[] = []
  const qId = question.id ?? 'UNKNOWN'
  const fId = question.familyId ?? 'UNKNOWN'

  if (!question.familyId) return issues
  if (!isFeatureEnabled('enforceSupportCoverage')) return issues

  const supportPolicy = getSupportPolicy(question.familyId)
  if (!supportPolicy) return issues

  // ── Hints required check ─────────────────────────────────────────────────

  if (supportPolicy.hintsRequired) {
    // We can't check hint availability from the question alone —
    // hints are in familySupportRegistry. This is an info-level note.
    issues.push(
      info(
        'support',
        STAGE,
        'SUPPORT_HINTS_EXPECTED',
        `Family "${question.familyId}" is expected to have hints in the support registry`,
        {
          questionId: qId,
          familyId: fId,
          severity: 10,
        },
      ),
    )
  }

  // ── Explanation required check ───────────────────────────────────────────

  if (supportPolicy.explanationRequired) {
    const hasExplanation = question.feedback?.explanationText?.trim()
    if (!hasExplanation) {
      issues.push(
        coach(
          'support',
          STAGE,
          'SUPPORT_EXPLANATION_REQUIRED',
          `Family "${question.familyId}" requires an explanation in feedback`,
          {
            field: 'feedback.explanationText',
            questionId: qId,
            familyId: fId,
            severity: 50,
          },
        ),
      )
    }
  }

  // ── Visual support required check ────────────────────────────────────────

  if (supportPolicy.visualSupportRequired) {
    const hasVisualSupport =
      Array.isArray(question.supportAids) && question.supportAids.length > 0
    if (!hasVisualSupport) {
      issues.push(
        coach(
          'support',
          STAGE,
          'SUPPORT_VISUAL_REQUIRED',
          `Family "${question.familyId}" requires visual support aids`,
          {
            field: 'supportAids',
            questionId: qId,
            familyId: fId,
            severity: 45,
          },
        ),
      )
    }
  }

  return issues
}
